import {
  Card,
  Chip,
  cn,
} from '@heroui/react'
import { Icon } from '@iconify/react'
import React from 'react'
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis } from 'recharts'

/**
 * Formats values for display according to their type
 */
function formatValue(value, type) {
  // Handle null or undefined values
  if (value == null) {
    return type === 'money'
      ? '$0.00'
      : type === 'duration'
        ? '0:00'
        : type === 'percentage' ? '0%' : '0'
  }

  if (type === 'number') {
    if (value >= 1000000) {
      return `${(value / 1000000).toFixed(1)}M`
    }
    else if (value >= 1000) {
      return `${(value / 1000).toFixed(0)}k`
    }
    return value.toLocaleString()
  }
  if (type === 'percentage')
    return `${value}%`
  if (type === 'money')
    return `$${value.toFixed(2)}`
  if (type === 'duration') {
    // Convert minutes to minutes:seconds format
    const minutes = Math.floor(value)
    const seconds = Math.round((value - minutes) * 60)
    return `${minutes}:${seconds.toString().padStart(2, '0')}`
  }

  return value
}

/**
 * Formats date strings according to time series type
 */
function formatDate(dateString, timeSeriesType, dataPoint = null) {
  try {
    // Special case for weekly format with weekStart and weekEnd fields
    if (timeSeriesType === 'weekly' && dataPoint?.weekStart && dataPoint?.weekEnd) {
      const startDate = new Date(dataPoint.weekStart)
      const endDate = new Date(dataPoint.weekEnd)

      const startFormatter = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' })
      const endFormatter = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' })

      return `${startFormatter.format(startDate)} - ${endFormatter.format(endDate)}`
    }

    // Regular date formatting based on time series type
    const jsDate = new Date(dateString)
    const options = { month: 'short', day: 'numeric' }

    if (timeSeriesType === 'hourly') {
      return new Intl.DateTimeFormat('en-US', {
        ...options,
        hour: 'numeric',
        hour12: true,
      }).format(jsDate)
    }

    if (timeSeriesType === 'weekly') {
      // Fallback for weekly without weekStart/weekEnd
      const weekEndDate = new Date(jsDate)
      weekEndDate.setDate(jsDate.getDate() + 6)

      return `${new Intl.DateTimeFormat('en-US', options).format(jsDate)} - 
              ${new Intl.DateTimeFormat('en-US', options).format(weekEndDate)}`
    }

    // Default daily format
    return new Intl.DateTimeFormat('en-US', options).format(jsDate)
  }
  catch (error) {
    console.error('Error formatting date:', error, dateString)
    return String(dateString) || ''
  }
}

/**
 * Safely checks if a date string is valid
 */
function isValidDate(dateString) {
  if (!dateString || typeof dateString !== 'string')
    return false

  try {
    return !Number.isNaN(new Date(dateString).getTime())
  }
  catch {
    return false
  }
}

/**
 * Call Analytics Chart Component
 * Displays a chart with metrics for call data
 */
export default function MegaChart({ analyticsData, isFetching }) {
  const [activeChart, setActiveChart] = React.useState('totalCalls')

  const { currentPeriod, previousPeriod, timeSeriesType = 'daily' } = analyticsData

  // Access timeSeries data from the nested structure
  const currentTimeseries = currentPeriod?.timeSeries || []
  const previousTimeseries = previousPeriod?.timeSeries || []

  // Define the metrics we want to display
  const metricsConfig = [
    {
      key: 'totalCalls',
      title: 'Total Calls',
      suffix: 'calls',
      type: 'number',
      getCurrentValue: () => currentPeriod.totalCalls ?? 0,
      getPreviousValue: () => previousPeriod.totalCalls ?? 0,
      getDailyValue: day => day.totalCalls ?? 0,
    },
    {
      key: 'totalCost',
      title: 'Total Cost',
      suffix: 'cost',
      type: 'money',
      getCurrentValue: () => {
        const price = currentPeriod.totalCost?._sum?.price
        return price != null ? Number.parseFloat(price) : 0
      },
      getPreviousValue: () => {
        const price = previousPeriod.totalCost?._sum?.price
        return price != null ? Number.parseFloat(price) : 0
      },
      getDailyValue: day => day.totalCost ?? 0,
    },
    {
      key: 'avgDuration',
      title: 'Avg Duration',
      suffix: 'minutes',
      type: 'duration',
      getCurrentValue: () => currentPeriod.avgDuration?._avg?.call_length ?? 0,
      getPreviousValue: () => previousPeriod.avgDuration?._avg?.call_length ?? 0,
      getDailyValue: day => day.avgDuration ?? 0,
    },
    {
      key: 'totalTransfers',
      title: 'Total Transfers',
      suffix: 'transfers',
      type: 'number',
      getCurrentValue: () => currentPeriod.totalTransfers ?? 0,
      getPreviousValue: () => previousPeriod.totalTransfers ?? 0,
      getDailyValue: day => day.totalTransfers ?? 0,
    },
  ]

  // Calculate percent changes and add to metrics config
  const metricsWithChanges = metricsConfig.map((metric) => {
    const currentValue = metric.getCurrentValue()
    const previousValue = metric.getPreviousValue()

    let percentChange = 0
    let changeType = 'neutral'

    if (previousValue && previousValue !== 0) {
      percentChange = ((currentValue - previousValue) / previousValue) * 100

      if (percentChange > 0) {
        changeType = 'positive'
      }
      else if (percentChange < 0) {
        changeType = 'negative'
      }
    }

    return {
      ...metric,
      value: currentValue,
      change: `${Math.abs(percentChange).toFixed(1)}%`,
      changeType,
    }
  })

  // Find the active metric config
  const activeMetric = metricsWithChanges.find(metric => metric.key === activeChart) || metricsWithChanges[0]

  // Prepare chart data for the active metric using the nested timeSeries data
  const chartData = currentTimeseries.map((dataPoint, index) => {
    const previousDataPoint = previousTimeseries[index] || null

    // For weekly data, we need to use weekStart and weekEnd fields
    let dateValue, formattedDate
    if (timeSeriesType === 'weekly' && dataPoint.weekStart) {
      dateValue = dataPoint.weekStart
      formattedDate = formatDate(dateValue, timeSeriesType, dataPoint)
    }
    else {
      // Check for datetime field first, then date field as fallback
      dateValue = dataPoint.datetime || dataPoint.date
      // Ensure the date is valid before formatting
      formattedDate = isValidDate(dateValue)
        ? formatDate(dateValue, timeSeriesType, dataPoint)
        : `Data point ${index + 1}`
    }

    return {
      date: dateValue,
      formattedDate,
      value: activeMetric.getDailyValue(dataPoint),
      previousValue: previousDataPoint ? activeMetric.getDailyValue(previousDataPoint) : 0,
    }
  }).sort((a, b) => {
    try {
      return new Date(a.date) - new Date(b.date)
    }
    catch {
      return 0
    }
  })

  // Determine chart color based on change type
  const chartColor = activeMetric.changeType === 'positive'
    ? 'success'
    : activeMetric.changeType === 'negative'
      ? 'danger'
      : 'default'

  return (
    <Card
      radius="sm"
      as="dl"
      className={cn(`
        overflow-visible border-2 shadow-none
        dark:border-default-100
      `, {
        'animate-pulse': isFetching,
      })}
    >
      <section className="flex flex-col flex-nowrap">
        <div className={`
          flex flex-col justify-start gap-2 p-3
          lg:flex-row lg:justify-between
        `}
        >
          <div className="flex items-center">
            <div className={`
              -my-3 flex w-full max-w-[800px] items-center gap-x-3
              overflow-x-auto py-3
            `}
            >
              {metricsWithChanges.map(({ key, change, changeType, type, value, title }) => (
                <button
                  type="button"
                  key={key}
                  className={cn(
                    'flex w-full flex-col gap-2 p-3 transition-colors',
                    {
                      'bg-default-100': activeChart === key,
                    },
                  )}
                  onClick={() => setActiveChart(key)}
                >
                  <span
                    className={cn(`
                      font-taurus text-xs tracking-wide text-nowrap
                      text-default-500 uppercase transition-colors
                    `, {
                      'font-medium text-default-700': activeChart === key,
                    })}
                  >
                    {title}
                  </span>
                  <div className="flex items-center gap-x-3">
                    <span className={cn('text-2xl text-foreground', {
                      'font-medium text-black': activeChart === key,
                    })}
                    >
                      {formatValue(value, type)}
                    </span>
                    <Chip
                      classNames={{
                        content: 'font-medium',
                      }}
                      color={
                        changeType === 'positive'
                          ? 'success'
                          : changeType === 'negative'
                            ? 'danger'
                            : 'default'
                      }
                      radius="sm"
                      size="sm"
                      startContent={(
                        <Icon
                          height={16}
                          icon={
                            changeType === 'positive'
                              ? 'solar:arrow-right-up-linear'
                              : changeType === 'negative'
                                ? 'solar:arrow-right-down-linear'
                                : 'solar:arrow-right-linear'
                          }
                          width={16}
                        />
                      )}
                      variant="flat"
                    >
                      <span>{change}</span>
                    </Chip>
                  </div>
                </button>
              ))}
            </div>

          </div>
        </div>
        <ResponsiveContainer
          className={`
            min-h-[300px]
            [&_.recharts-surface]:outline-hidden
          `}
          height="100%"
          width="100%"
        >
          <AreaChart
            accessibilityLayer
            data={chartData}
            height={300}
            margin={{
              left: 0,
              right: 0,
            }}
            width={500}
          >
            <defs>
              <linearGradient id="colorGradient" x1="0" x2="0" y1="0" y2="10">
                <stop
                  offset="10%"
                  stopColor={`hsl(var(--heroui-${chartColor}-500))`}
                  stopOpacity={0.3}
                />
                <stop
                  offset="100%"
                  stopColor={`hsl(var(--heroui-${chartColor}-100))`}
                  stopOpacity={0.1}
                />
              </linearGradient>
            </defs>
            <CartesianGrid
              horizontalCoordinatesGenerator={() => [200, 150, 100, 50]}
              stroke="hsl(var(--heroui-default-200))"
              strokeDasharray="3 3"
              vertical={false}
            />
            <XAxis
              axisLine
              dataKey="formattedDate"
              style={{ fontSize: 'var(--heroui-font-size-tiny)', transform: 'translateX(-40px)' }}
              tickLine
              interval="preserveStartEnd"
            />
            <Tooltip
              content={({ label, payload }) => (
                <div className={`
                  flex h-auto min-w-[120px] items-center gap-x-2 rounded-medium
                  bg-foreground p-2 text-tiny shadow-small
                `}
                >
                  <div className="flex w-full flex-col gap-y-0">
                    {payload?.map((p, index) => {
                      if (!p || p.value === 0)
                        return null

                      return (
                        <div
                          key={`${index}-${p.name}`}
                          className="flex w-full items-center gap-x-2"
                        >
                          <div className={`
                            flex w-full items-center gap-x-1 text-small
                            text-background
                          `}
                          >
                            <span>{formatValue(p.value, activeMetric.type)}</span>
                            <span>{activeMetric.suffix}</span>
                            {p.name === 'previousValue' && (
                              <span className="text-tiny opacity-70"> (previous)</span>
                            )}
                          </div>
                        </div>
                      )
                    })}
                    <span className="text-small font-medium text-foreground-400">
                      {label}
                    </span>
                  </div>
                </div>
              )}
              cursor={{
                strokeWidth: 0,
              }}
            />
            <Area
              activeDot={{
                stroke: `hsl(var(--heroui-${chartColor === 'default' ? 'foreground' : chartColor}))`,
                strokeWidth: 2,
                fill: 'hsl(var(--heroui-background))',
                r: 5,
              }}
              animationDuration={1000}
              animationEasing="ease"
              dataKey="value"
              fill="url(#colorGradient)"
              stroke={`hsl(var(--heroui-${chartColor === 'default' ? 'foreground' : chartColor}))`}
              strokeWidth={2}
              type="monotone"
              name="Current"
            />
            <Area
              activeDot={{
                stroke: 'hsl(var(--heroui-default-400))',
                strokeWidth: 2,
                fill: 'hsl(var(--heroui-background))',
                r: 5,
              }}
              animationDuration={1000}
              animationEasing="ease"
              dataKey="previousValue"
              fill="transparent"
              stroke="hsl(var(--heroui-default-400))"
              strokeWidth={2}
              type="monotone"
              name="previousValue"
            />
          </AreaChart>
        </ResponsiveContainer>
      </section>
    </Card>
  )
}
